import React, { useState, useCallback, useRef } from 'react';
import { useMsal } from '@azure/msal-react';
import toast from 'react-hot-toast';
import GPTMakerLanding from './GPTMakerLanding';
import StepIndicator from './StepIndicator';
import Phase1RoleIntake from './Phase1RoleIntake';
import Phase2ExpertDiscovery from './Phase2ExpertDiscovery';
import Phase3GPTProfile from './Phase3GPTProfile';
import Phase4Sources from './Phase4Sources';
import Phase5Output from './Phase5Output';
import {
  createConversation,
  analyzeExperts,
  buildGPTProfile,
  collectSources,
  saveAsAgent,
  generateFinalGPT,
} from './gptMakerService';
import './GPTMaker.css';

const isDev = process.env.NODE_ENV === 'development';
const log = (...args) => { if (isDev) console.log(...args); };

export default function GPTMaker() {
  const { accounts } = useMsal();
  const userEmail = accounts[0]?.username || '';

  // Step management: 0=landing, 1-5=phases
  const [step, setStep] = useState(0);
  const [initialPrompt, setInitialPrompt] = useState('');

  // Data state
  const [roleData, setRoleData] = useState(null);
  const [expertResult, setExpertResult] = useState(null);
  const [selectedExperts, setSelectedExperts] = useState([]);
  const [gptProfile, setGptProfile] = useState(null);
  const [sourcesData, setSourcesData] = useState(null);
  const [finalOutput, setFinalOutput] = useState(null);

  // Loading/error state
  const [loading, setLoading] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState('');
  const [error, setError] = useState(null);
  const [agentSaved, setAgentSaved] = useState(false);

  // Conversation ID for AI context
  const conversationId = useRef(null);

  const ensureConversation = useCallback(async () => {
    if (!conversationId.current) {
      const conv = await createConversation(userEmail);
      conversationId.current = conv.id;
    }
    return conversationId.current;
  }, [userEmail]);

  // Landing → Phase 1
  const handleLandingSubmit = (text) => {
    setInitialPrompt(text);
    setStep(1);
  };

  // Phase 1 → Phase 2: Analyze experts
  const handleRoleSubmit = useCallback(async (data) => {
    setRoleData(data);
    setStep(2);
    setLoading(true);
    setLoadingMessage('Gesprek aanmaken...');
    setError(null);
    try {
      log('[GPTMaker] Creating conversation...');
      const convId = await ensureConversation();
      log('[GPTMaker] Conversation created:', convId);
      setLoadingMessage('Experts worden geanalyseerd... (dit kan tot 60 seconden duren)');
      log('[GPTMaker] Sending expert analysis prompt...');
      const result = await analyzeExperts(data, convId, userEmail);
      log('[GPTMaker] Expert result:', result);
      setExpertResult(result);
    } catch (err) {
      log('[GPTMaker] Expert analysis failed:', err);
      setError(`Fout bij expert-analyse: ${err.message || 'Onbekende fout'}. Check de browser console voor details.`);
    } finally {
      setLoading(false);
      setLoadingMessage('');
    }
  }, [ensureConversation, userEmail]);

  // Phase 2 → Phase 3: Build GPT profile
  const handleExpertsConfirm = useCallback(async (experts) => {
    setSelectedExperts(experts);
    setStep(3);
    setLoading(true);
    setLoadingMessage('GPT-profiel wordt samengesteld...');
    setError(null);
    try {
      const convId = await ensureConversation();
      const profile = await buildGPTProfile(roleData, experts, convId, userEmail);
      log('[GPTMaker] Profile:', profile);
      setGptProfile(profile);
    } catch (err) {
      log('[GPTMaker] Profile build failed:', err);
      setError(`Fout bij profiel: ${err.message}`);
    } finally {
      setLoading(false);
      setLoadingMessage('');
    }
  }, [ensureConversation, roleData, userEmail]);

  // Phase 3 → Phase 4: Collect sources
  const handleProfileConfirm = useCallback(async () => {
    setStep(4);
    setLoading(true);
    setLoadingMessage('Bronnen worden verzameld en gestructureerd...');
    setError(null);
    try {
      const convId = await ensureConversation();
      const sources = await collectSources(roleData, selectedExperts, gptProfile, convId, userEmail);
      log('[GPTMaker] Sources:', sources);
      setSourcesData(sources);
    } catch (err) {
      log('[GPTMaker] Source collection failed:', err);
      setError(`Fout bij bronnen: ${err.message}`);
    } finally {
      setLoading(false);
      setLoadingMessage('');
    }
  }, [ensureConversation, roleData, selectedExperts, gptProfile, userEmail]);

  // Phase 4 → Phase 5: Generate final GPT
  const handleSourcesConfirm = useCallback(async () => {
    setStep(5);
    setLoading(true);
    setLoadingMessage('Super GPT wordt gegenereerd...');
    setError(null);
    try {
      const convId = await ensureConversation();
      const output = await generateFinalGPT(roleData, selectedExperts, gptProfile, sourcesData, convId, userEmail);
      log('[GPTMaker] Final output:', output);
      setFinalOutput(output);
    } catch (err) {
      log('[GPTMaker] GPT generation failed:', err);
      setError(`Fout bij genereren: ${err.message}`);
    } finally {
      setLoading(false);
      setLoadingMessage('');
    }
  }, [ensureConversation, roleData, selectedExperts, gptProfile, sourcesData, userEmail]);

  // Save as AI Chat agent
  const handleSaveAsAgent = useCallback(async () => {
    try {
      await saveAsAgent(gptProfile, finalOutput, userEmail);
      setAgentSaved(true);
      toast.success(`"${gptProfile.naam}" is toegevoegd aan AI Chat!`);
    } catch (err) {
      log('[GPTMaker] Save agent failed:', err);
      toast.error(`Opslaan mislukt: ${err.message}`);
      throw err;
    }
  }, [gptProfile, finalOutput, userEmail]);

  // Restart
  const handleRestart = () => {
    setStep(0);
    setInitialPrompt('');
    setRoleData(null);
    setExpertResult(null);
    setSelectedExperts([]);
    setGptProfile(null);
    setSourcesData(null);
    setFinalOutput(null);
    setAgentSaved(false);
    setLoading(false);
    setError(null);
    conversationId.current = null;
  };

  // Go back one step
  const goBack = (toStep) => {
    setError(null);
    setStep(toStep);
  };

  return (
    <div className="gpt-maker">
      {step === 0 ? (
        <GPTMakerLanding onSubmit={handleLandingSubmit} />
      ) : (
        <>
          <StepIndicator currentStep={step} />

          {step === 1 && (
            <Phase1RoleIntake
              initialPrompt={initialPrompt}
              onSubmit={handleRoleSubmit}
            />
          )}

          {step === 2 && (
            <Phase2ExpertDiscovery
              experts={expertResult?.experts}
              aanbeveling={expertResult?.aanbeveling}
              gptRichting={expertResult?.gptRichting}
              loading={loading}
              loadingMessage={loadingMessage}
              error={error}
              onConfirm={handleExpertsConfirm}
              onBack={() => goBack(1)}
            />
          )}

          {step === 3 && (
            <Phase3GPTProfile
              profile={gptProfile}
              selectedExperts={selectedExperts}
              loading={loading}
              loadingMessage={loadingMessage}
              error={error}
              onConfirm={handleProfileConfirm}
              onBack={() => goBack(2)}
            />
          )}

          {step === 4 && (
            <Phase4Sources
              sources={sourcesData}
              loading={loading}
              loadingMessage={loadingMessage}
              error={error}
              onConfirm={handleSourcesConfirm}
              onBack={() => goBack(3)}
            />
          )}

          {step === 5 && (
            <Phase5Output
              output={finalOutput}
              gptProfile={gptProfile}
              loading={loading}
              loadingMessage={loadingMessage}
              error={error}
              onBack={() => goBack(4)}
              onRestart={handleRestart}
              onSaveAsAgent={handleSaveAsAgent}
              agentSaved={agentSaved}
              onUpdateOutput={setFinalOutput}
              onUpdateProfile={setGptProfile}
            />
          )}
        </>
      )}
    </div>
  );
}
